import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Link, useLocation } from "react-router-dom";
import { NavigationMenu, NavigationMenuItem, NavigationMenuList, navigationMenuTriggerStyle } from "@/src/components/ui/navigation-menu";
import { cn } from "@/src/lib/utils";
import { FloatingWhatsApp } from "@/src/components/FloatingWhatsApp";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { href: "/", label: "Home" },
  { href: "#about", label: "About" },
  { href: "/workshops", label: "Workshops" },
  { href: "/faq", label: "FAQ" },
  { href: "#testimonials", label: "Testimonials" },
  { href: "#services", label: "Services" },
  { href: "#contact", label: "Contact" },
];

export function Header() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  const navLink = (href: string, label: string, mobile = false) => {
    const isAnchor = href.startsWith("#");
    const baseClass = mobile
      ? "block py-3 px-4 text-lg font-medium hover:text-primary transition-colors border-b border-primary/10 last:border-0"
      : cn(navigationMenuTriggerStyle(), "bg-transparent");

    if (isAnchor && !isHome) {
      return (
        <Link to={`/${href}`} className={baseClass} onClick={closeMobile}>
          {label}
        </Link>
      );
    }

    if (isAnchor) {
      return (
        <a href={href} className={baseClass} onClick={closeMobile}>
          {label}
        </a>
      );
    }

    return (
      <Link
        to={href}
        className={cn(baseClass, !mobile && location.pathname === href && "text-primary font-bold")}
        onClick={closeMobile}
      >
        {label}
      </Link>
    );
  };

  return (
    <>
      <FloatingWhatsApp />
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 glass"
      >
        {/* Main Header Row */}
        <div className="flex items-center justify-between px-6 py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" onClick={closeMobile}>
            <img
              src="/logo.png"
              alt="Iwantcoaching Logo"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              {NAV_ITEMS.map((item) => (
                <NavigationMenuItem key={item.href}>
                  {navLink(item.href, item.label)}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Hamburger Button (mobile only) */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl hover:bg-primary/10 transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((prev) => !prev)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              key="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden md:hidden bg-background/95 backdrop-blur border-t border-primary/10"
            >
              <nav className="px-4 py-2">
                {NAV_ITEMS.map((item) => (
                  <div key={item.href}>
                    {navLink(item.href, item.label, true)}
                  </div>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
}
